'use client';

// ══════════════════════════════════════════════════════════════
// Chat publico de la landing: el agente de John Fox vendiendose solo.
//
// Backend: WF Web-Chat (webhook /web-chat) — prompt del canal 'web' del
// tenant john_fox + RAG sobre su conocimiento. Cuando el visitante deja
// email o telefono, el backend crea el prospecto en 'lead_capturado'.
//
// Identidad: UUID propio en localStorage (jf_chat_sid). Sin cookies de
// terceros ni fingerprinting. El historial vive en sessionStorage y
// viaja en cada request: el backend no guarda estado hasta que hay lead.
// ══════════════════════════════════════════════════════════════

import { useState, useRef, useEffect, useCallback } from 'react';
import { Bot, X, Send, Sparkles } from 'lucide-react';

const ENDPOINT =
  process.env.NEXT_PUBLIC_WEBCHAT_URL || 'https://agent.johnfox.click/webhook/web-chat';

const SALUDO =
  'Hola. Soy el agente de John Fox: el mismo tipo de asistente que construimos para nuestros clientes. Preguntame lo que quieras sobre como automatizar tu negocio.';

const SUGERENCIAS = [
  'Como funciona?',
  'Sirve para mi rubro?',
  'Cuanto tarda la implementacion?',
];

const MAX_TURNOS = 30;

interface Turno {
  rol: 'cliente' | 'agente';
  texto: string;
  error?: boolean;
}

function nuevoId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function ChatWidget() {
  const [abierto, setAbierto] = useState(false);
  const [mensajes, setMensajes] = useState<Turno[]>([]);
  const [input, setInput] = useState('');
  const [cargando, setCargando] = useState(false);
  const [sid, setSid] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Identidad + historial de la sesion
  useEffect(() => {
    let id = '';
    try {
      id = localStorage.getItem('jf_chat_sid') || '';
      if (!id) {
        id = nuevoId();
        localStorage.setItem('jf_chat_sid', id);
      }
      const prev = sessionStorage.getItem('jf_chat_hist');
      if (prev) {
        const parsed = JSON.parse(prev);
        if (Array.isArray(parsed)) setMensajes(parsed);
      }
    } catch {
      id = nuevoId();
    }
    setSid(id);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [mensajes, cargando, abierto]);

  const guardar = (turnos: Turno[]) => {
    try {
      sessionStorage.setItem('jf_chat_hist', JSON.stringify(turnos.slice(-40)));
    } catch {
      /* sessionStorage puede estar bloqueado: el chat sigue andando */
    }
  };

  const enviar = useCallback(
    async (texto: string) => {
      const limpio = texto.trim();
      if (!limpio || cargando) return;

      if (mensajes.length >= MAX_TURNOS * 2) {
        setMensajes((p) => [
          ...p,
          {
            rol: 'agente',
            texto: 'Charlamos bastante por aca. Dejanos tu mail o escribinos por WhatsApp y seguimos.',
          },
        ]);
        return;
      }

      const historial = mensajes.map((m) => ({ rol: m.rol, texto: m.texto }));
      const conMio: Turno[] = [...mensajes, { rol: 'cliente', texto: limpio }];
      setMensajes(conMio);
      guardar(conMio);
      setInput('');
      setCargando(true);

      try {
        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sid,
            mensaje: limpio,
            historial: historial.slice(-12),
            page_url: typeof window !== 'undefined' ? window.location.href : '',
          }),
        });
        const data = (await res.json()) as { success?: boolean; respuesta?: string; error?: string };
        const turno: Turno =
          data?.success && data.respuesta
            ? { rol: 'agente', texto: data.respuesta }
            : { rol: 'agente', texto: data?.error || 'No pude responder ahora. Probá de nuevo.', error: true };
        const final = [...conMio, turno];
        setMensajes(final);
        guardar(final);
      } catch {
        setMensajes((p) => [
          ...p,
          { rol: 'agente', texto: 'Se cortó la conexión. Probá de nuevo.', error: true },
        ]);
      } finally {
        setCargando(false);
        inputRef.current?.focus();
      }
    },
    [cargando, mensajes, sid]
  );

  const abrir = () => {
    setAbierto(true);
    setTimeout(() => inputRef.current?.focus(), 120);
  };

  const mostrarSugerencias = mensajes.length === 0 && !cargando;

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => (abierto ? setAbierto(false) : abrir())}
        aria-label={abierto ? 'Cerrar chat' : 'Abrir chat con el agente'}
        className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 bg-gradient-to-br from-[#FF6B35] to-[#FF8E53] shadow-lg shadow-[#FF6B35]/30"
      >
        {abierto ? (
          <X className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        ) : (
          <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
        )}
        {!abierto && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#10B981] ring-2 ring-white dark:ring-[#0A0F29]" />
        )}
      </button>

      {/* Panel */}
      {abierto && (
        <div className="fixed z-50 flex flex-col overflow-hidden bg-white dark:bg-[#0A0F29] border border-gray-200 dark:border-white/10 shadow-2xl shadow-black/20 bottom-0 inset-x-0 h-[85dvh] rounded-t-2xl sm:bottom-40 sm:right-6 sm:inset-x-auto sm:w-[380px] sm:h-[520px] sm:rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#FF6B35] to-[#FF8E53]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm leading-tight">Agente John Fox</p>
                <p className="text-white/80 text-[11px] leading-tight">Respondo al toque</p>
              </div>
            </div>
            <button onClick={() => setAbierto(false)} aria-label="Cerrar" className="text-white/90 hover:text-white transition">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Mensajes */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-white/[0.06] px-3.5 py-2.5 text-sm text-gray-700 dark:text-white/70 leading-relaxed">
                {SALUDO}
              </div>
            </div>

            {mensajes.map((m, i) => (
              <div key={i} className={m.rol === 'cliente' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={
                    m.rol === 'cliente'
                      ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-[#FF6B35] px-3.5 py-2.5 text-sm text-white leading-relaxed whitespace-pre-wrap'
                      : m.error
                        ? 'max-w-[85%] rounded-2xl rounded-bl-sm bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3.5 py-2.5 text-sm text-red-600 dark:text-red-400 leading-relaxed'
                        : 'max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-white/[0.06] px-3.5 py-2.5 text-sm text-gray-700 dark:text-white/70 leading-relaxed whitespace-pre-wrap'
                  }
                >
                  {m.texto}
                </div>
              </div>
            ))}

            {mostrarSugerencias && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGERENCIAS.map((s) => (
                  <button
                    key={s}
                    onClick={() => enviar(s)}
                    className="rounded-full border border-gray-200 dark:border-white/10 px-3 py-1.5 text-xs text-gray-600 dark:text-white/60 hover:border-[#FF6B35]/40 hover:text-[#FF6B35] transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {cargando && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm bg-gray-100 dark:bg-white/[0.06] px-4 py-3 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-white/40 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-white/40 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-white/40 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-white/10 p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    enviar(input);
                  }
                }}
                rows={1}
                placeholder="Escribí tu consulta..."
                disabled={cargando}
                className="flex-1 resize-none max-h-24 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-white/25 focus:outline-none focus:border-[#FF6B35]/50 transition"
              />
              <button
                onClick={() => enviar(input)}
                disabled={cargando || !input.trim()}
                aria-label="Enviar"
                className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#FF8E53] text-white flex items-center justify-center transition hover:shadow-lg hover:shadow-[#FF6B35]/25 disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="flex items-center justify-center gap-1 text-[10px] text-gray-400 dark:text-white/20 mt-2">
              <Sparkles className="h-2.5 w-2.5" />
              Asistente de IA — no compartas datos sensibles
            </p>
          </div>
        </div>
      )}
    </>
  );
}
